import { useState, useMemo, useCallback } from "react";
import { formatarDataSemFuso } from "../../utils/formatDate";

export function useTableLogic(data, columns) {
  const [sort, setSort] = useState(null);
  const [filters, setFilters] = useState({});

  // Apply filters
  const filteredData = useMemo(() => {
    return data.filter((row) => {
      return Object.entries(filters).every(([key, selectedValues]) => {
        if (selectedValues.size === 0) return true;

        const column = columns.find(c => c.key === key);
        const cellValue = formatValue(row[key], column);

        return selectedValues.has(cellValue);
      });
    });
  }, [data, filters, columns]);

  // Apply sort
  const sortedData = useMemo(() => {
    if (!sort || !sort.direction) return filteredData;

    const { key, direction } = sort;

    return [...filteredData].sort((a, b) => {
      const aVal = a[key];
      const bVal = b[key];

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (typeof aVal === "number" && typeof bVal === "number") {
        return direction === "asc" ? aVal - bVal : bVal - aVal;
      }

      const aStr = String(aVal);
      const bStr = String(bVal);

      return direction === "asc"
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });
  }, [filteredData, sort]);

  // Excel-like cascading filters
  const getFilterOptions = useCallback(
    (columnKey) => {
      const column = columns.find(c => c.key === columnKey);
      const otherFilters = { ...filters };
      delete otherFilters[columnKey];

      const cascadedData = data.filter((row) =>
        Object.entries(otherFilters).every(([key, selectedValues]) => {
          if (selectedValues.size === 0) return true;

          const col = columns.find((c) => c.key === key);
          const value = formatCellForFilter(row[key], col);

          return selectedValues.has(value);
        })
      );


      const uniqueValues = new Set(
        cascadedData.map((row) =>
          formatValue(row[columnKey], column)
        )
      );

    return Array.from(uniqueValues).sort((a, b) =>
  a.localeCompare(b, "pt-BR", { numeric: true })
);
    },
    [data, filters, columns]
  );

  const toggleSort = useCallback((key) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, direction: "asc" };
      if (prev.direction === "asc") return { key, direction: "desc" };
      return null;
    });
  }, []);

  const toggleFilter = useCallback((columnKey, value) => {
    setFilters((prev) => {
      const current = new Set(prev[columnKey] || []);

      if (current.has(value)) {
        current.delete(value);
      } else {
        current.add(value);
      }

      return { ...prev, [columnKey]: current };
    });
  }, []);

  const selectAllFilter = useCallback((columnKey, values) => {
    setFilters((prev) => ({
      ...prev,
      [columnKey]: new Set(values),
    }));
  }, []);

  const clearFilter = useCallback((columnKey) => {
    setFilters((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilters({});
  }, []);

  const activeFilterCount = Object.values(filters).filter(
    (s) => s.size > 0
  ).length;

  function formatCellForFilter(value, column) {
    if (value == null) return "(vazio)";

    switch (column?.format) {
      case "percent":
        return typeof value === "number"
          ? `${value.toFixed(2)}%`
          : String(value);

      case "currency":
        return typeof value === "number"
          ? value.toLocaleString("pt-BR", {
            style: "currency",
            currency: "BRL",
          })
          : String(value);

      case "number":
        return typeof value === "number"
          ? value.toLocaleString("pt-BR")
          : String(value);

      case "date":
        return typeof value === "string"
          ? formatarDataSemFuso(value)
          : String(value);

      case "boolean":
        return value ? "Sim" : "Não";

      default:
        return String(value);
    }
  }

  return {
    processedData: sortedData,
    sort,
    filters,
    toggleSort,
    toggleFilter,
    selectAllFilter,
    clearFilter,
    clearAllFilters,
    getFilterOptions,
    activeFilterCount,
    totalRows: data.length,
    filteredRows: filteredData.length,
  };
}
function formatValue(value, column) {
  if (value == null) return "(Vazio)";

  // 👇 tratar arrays (ex: auxiliares)
  if (Array.isArray(value)) {
    return value
      .map(v => {
        if (typeof v === "object") {
          return v.nome ?? v.label ?? v.name ?? v.id ?? "";
        }
        return String(v);
      })
      .join(", ");
  }

  // 👇 tratar objeto único
  if (typeof value === "object") {
    return value.nome ?? value.label ?? value.name ?? value.id ?? String(value);
  }

  switch (column?.format) {
    case "percent":
      return typeof value === "number"
        ? `${value.toFixed(2)}%`
        : String(value);

    case "currency":
      return typeof value === "number"
        ? value.toLocaleString("pt-BR", {
            style: "currency",
            currency: "BRL",
          })
        : String(value);

    case "number":
      return typeof value === "number"
        ? value.toLocaleString("pt-BR")
        : String(value);

    case "date":
      return typeof value === "string"
        ? formatarDataSemFuso(value)
        : String(value);

    case "boolean":
      return value ? "Sim" : "Não";

    default:
      return String(value);
  }
}